import { Card, CardContent } from '@ui/card.tsx';
import { type Option, SelectTagInput } from '@r/components/kpi/filters/select-tag-input.tsx';
import { type FC, useState, useContext, useEffect, useMemo } from 'react';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { useTranslation } from 'react-i18next';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { isTypologyConversion } from '@r/utils/workspace-utils.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { DateRangePicker } from '@r/components/ui/date-range-picker.tsx';
import { format as formatDate } from 'date-fns';
import { companyStatuses } from '@r/types/index.ts';
import { canUseSalesFilter, getFilterableRoles, roleToI18nKey, type Role } from '@r/utils/roles.ts';
import { useApiAdminusersGetCollection } from '@r/api/generated/client.ts';
import type { UserUserCollectionReadModel } from '@r/api/generated/client.schemas.ts';

type KpiFilterBarProps = {
  onStatusChange: (statuses: Option[]) => void;
  onTypologyChange: (newTags: Option[]) => void;
  companyTypologies: Option[];
  onSalesChange?: (ids: string[]) => void;
  onDateRangeChange?: (range: { from?: string; to?: string }) => void;
  dateRange?: { from?: string; to?: string };
  initialSelectedStatuses?: Option[];
  onMaxRoleChange?: (roles: Role[]) => void;
  initialSelectedRoles?: Role[];
};

const KpiFilterBar: FC<KpiFilterBarProps> = ({
  onStatusChange,
  onTypologyChange,
  companyTypologies,
  onSalesChange,
  onDateRangeChange,
  dateRange,
  initialSelectedStatuses,
  onMaxRoleChange,
  initialSelectedRoles
}) => {
  const { t } = useTranslation();
  const user = useUser();
  const { workspace, isWorkspaceSuccess } = UseWorkspace(user.data);
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const userWorkspaces = user.data?.visibleWorkspaces ?? [];
  const activeWorkspace = getActiveWorkspaceUniversal(userWorkspaces);
  const roles: string[] = (activeWorkspace?.roles as string[]) ?? [];
  const canUseSalesFilterValue = canUseSalesFilter(roles);

  const [selectedSalesAdmins, setSelectedSalesAdmins] = useState<Option[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<Option[]>(initialSelectedStatuses ?? []);

  const { data: salesAdminsResponse } = useApiAdminusersGetCollection(
    {
      'maxRole[]': ['ROLE_SALES_ADMIN'],
      perPage: 1000,
      'order[lastname]': 'asc'
    },
    {
      query: {
        queryKey: ['/admin/users', 'ROLE_SALES_ADMIN']
      }
    }
  );

  const salesAdminOptions: Option[] = useMemo(
    () =>
      (salesAdminsResponse?.data ?? []).reduce(
        (acc: Option[], user: UserUserCollectionReadModel) => {
          const uniqueCode =
            user.userWorkspaces?.find((w) => w.active)?.uniqueCode ||
            user.userWorkspaces?.[0]?.uniqueCode;
          if (typeof uniqueCode === 'string' && uniqueCode.length > 0) {
            const lastName = typeof user.lastname === 'string' ? user.lastname.toUpperCase() : '';
            const firstName = typeof user.firstname === 'string' ? user.firstname : '';
            acc.push({ id: uniqueCode, name: `${lastName} ${firstName}`.trim() });
          }
          return acc;
        },
        [] as Option[]
      ),
    [salesAdminsResponse?.data]
  );

  const filteredSalesAdminOptions: Option[] = useMemo(
    () =>
      salesAdminOptions.filter(
        (option) => !selectedSalesAdmins.some((selected) => selected.id === option.id)
      ),
    [salesAdminOptions, selectedSalesAdmins]
  );

  useEffect(() => {
    if (!onSalesChange) return;
    const uniqueCodes = selectedSalesAdmins.map((opt) => String(opt.id));
    onSalesChange(uniqueCodes);
  }, [onSalesChange, selectedSalesAdmins]);

  useEffect(() => {
    onStatusChange(selectedStatuses);
  }, [onStatusChange, selectedStatuses]);

  const filterableRoles = useMemo(
    () => getFilterableRoles(activeWorkspace?.maxRole as Role | undefined),
    [activeWorkspace]
  );

  const maxRoleOptions: Option[] = useMemo(
    () =>
      filterableRoles.map((role) => ({
        id: role,
        name: t(roleToI18nKey(role))
      })),
    [filterableRoles, t]
  );

  const [selectedMaxRoles, setSelectedMaxRoles] = useState<Role[]>(initialSelectedRoles ?? []);

  const selectedMaxRoleOptions: Option[] = useMemo(
    () => maxRoleOptions.filter((option) => selectedMaxRoles.includes(option.id as Role)),
    [maxRoleOptions, selectedMaxRoles]
  );

  const filteredMaxRoleOptions: Option[] = useMemo(
    () => maxRoleOptions.filter((option) => !selectedMaxRoles.includes(option.id as Role)),
    [maxRoleOptions, selectedMaxRoles]
  );

  useEffect(() => {
    if (!onMaxRoleChange) return;
    onMaxRoleChange(selectedMaxRoles);
  }, [onMaxRoleChange, selectedMaxRoles]);

  const typologyOptions: Option[] = useMemo(() => {
    if (!isWorkspaceSuccess || !workspace || !user.data) {
      return [];
    }
    return (workspace.typologies ?? []).map((typology) => ({
      id: typology.id,
      name: typology.name
    }));
  }, [isWorkspaceSuccess, workspace, user.data]);

  const companyStatusOptions: Option[] = useMemo(
    () =>
      companyStatuses.map((status) => ({
        id: status,
        name: t(status)
      })),
    [t]
  );

  const filteredStatusOptions: Option[] = useMemo(
    () =>
      companyStatusOptions.filter(
        (option) => !selectedStatuses.some((selected) => selected.id === option.id)
      ),
    [companyStatusOptions, selectedStatuses]
  );

  return (
    <Card className="mb-5">
      <CardContent className="flex flex-wrap gap-5 pt-5">
        <div className="flex flex-col space-y-1">
          <div className="text-xl font-bold whitespace-nowrap">
            {t('admin.kpi.filter.company.status.title')}
          </div>
          <SelectTagInput
            value={selectedStatuses}
            onChange={setSelectedStatuses}
            options={filteredStatusOptions}
          />
        </div>

        {isTypologyConversion(workspaceConfigContext) && (
          <div className="flex flex-col space-y-1">
            <div className="text-xl font-bold whitespace-nowrap">
              {t('admin.kpi.filter.typology.title')}
            </div>
            <SelectTagInput
              value={companyTypologies}
              onChange={onTypologyChange}
              options={typologyOptions}
            />
          </div>
        )}

        {canUseSalesFilterValue && (
          <div className="flex flex-col space-y-1">
            <div className="text-xl font-bold whitespace-nowrap">
              {t('admin.kpi.filter.sales.title')}
            </div>
            <SelectTagInput
              value={selectedSalesAdmins}
              onChange={setSelectedSalesAdmins}
              options={filteredSalesAdminOptions}
            />
          </div>
        )}

        {onMaxRoleChange && (
          <div className="flex flex-col space-y-1">
            <div className="text-xl font-bold whitespace-nowrap">
              {t('admin.kpi.filter.role.title')}
            </div>
            <SelectTagInput
              value={selectedMaxRoleOptions}
              onChange={(options) => {
                const roles = options.map((opt) => opt.id as Role);
                setSelectedMaxRoles(roles);
              }}
              options={filteredMaxRoleOptions}
            />
          </div>
        )}

        <div className="flex flex-col space-y-1">
          <div className="text-xl font-bold whitespace-nowrap">
            {t('admin.kpi.filter.date.title')}
          </div>
          <DateRangePicker
            isActive={Boolean(dateRange?.from && dateRange?.to)}
            placeholder={t('date.range.choose.period')}
            onUpdate={({ range }) => {
              if (!range.from || !range.to) {
                onDateRangeChange?.({});
                return;
              }
              onDateRangeChange?.({
                from: formatDate(range.from, 'yyyy-MM-dd'),
                to: formatDate(range.to, 'yyyy-MM-dd')
              });
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export { KpiFilterBar };
